/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {CurrentUserParams} from './GraphQL';
import {CurrentUser} from './types';

/**
 * Defines the default fields / attributes to be returned when issuing a
 * `getCurrentUserFields` Query.
 *
 * ```typescript
 *  import {getCurrentUserFields} from '@ion/services/src/graphql/DefaultFields';
 *  ...
 *
 *  try {
 *    // defines the default current user fields which are to be returned,
 *    // optionally passing additional fields to be returned in addition
 *    // to the defaults ...
 *    const user = await currentUser(getCurrentUserFields());
 *  } catch (err) {
 *    ...
 *  }
 * ```
 */
export const getCurrentUserFields = (
  fields?: Array<keyof CurrentUser>): CurrentUserParams => ({
  fields : [
    'id',
    'username',
    'firstName',
    'lastName',
    'email',
    'status',
    'version',
    'unitOfMeasure',
    'systemOfUnits',
    'lastModifiedOn',
    'createdOn',
    'timeZone',
    'passwordUpdated',
    'activationDate',
    'deletionDate', {
      account : [
        'id',
        'name',
        'status',
        'solomonId',
        'enterprise',
        'parentId',
        'status',
        'version', {
          extendedAttribute : [
            'value',
            'applicationKey'
          ]
        }
      ]
    }, {
      extendedAttribute : [
        'value',
        'applicationKey',
        'lastModifiedOn'
      ]
    }, {
      applications : [
        'id',
        'applicationKey',
        'name',
        'status'
      ]
    }, {
      roles : [
        'id',
        'name',
        'applicationKey',
        'status'
      ]
    }, {
      groups : [
        'id',
        'name',
        'status'
      ]
    }, {
      address : [
        'street',
        'street2',
        'city',
        'country',
        'stateProvinceCd'
      ]
    },
    ...(fields || [])
  ]
});
